import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { signOut } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

interface LayoutProps {
  onLogout: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles: string[];
}

const menuItems: MenuItem[] = [
  { text: 'Empresas', icon: <BusinessIcon />, path: '/empresas', roles: ['administrador'] },
  { text: 'Certificados', icon: <SecurityIcon />, path: '/certificados', roles: ['administrador'] },
  { text: 'Usuarios', icon: <PeopleIcon />, path: '/usuarios', roles: ['administrador'] },
  { text: 'Series', icon: <FormatListNumberedIcon />, path: '/series', roles: ['administrador', 'operador'] },
  { text: 'Comprobantes', icon: <DescriptionIcon />, path: '/comprobantes', roles: ['administrador', 'operador'] },
  { text: 'Crear Comprobante', icon: <AddIcon />, path: '/crear-comprobante', roles: ['administrador', 'operador'] },
];

export default function Layout({ onLogout }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, loading } = useAuth();
  
  // Obtener el rol del usuario desde el contexto
  const userRole = usuario?.rol || 'operador';

  // Filtrar menú según rol
  const filteredMenuItems = menuItems.filter(item => item.roles.includes(userRole));

  if (loading) {
    return null; // O un spinner de carga
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      // Primero hacer signOut de Amplify
      await signOut({ global: true });
      
      // Limpiar completamente el localStorage
      localStorage.clear();
      
      // Limpiar sessionStorage también
      sessionStorage.clear();
      
      // Llamar al callback de logout
      onLogout();
      
      // Redirigir a la página de login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Incluso si hay error, limpiar el storage y redirigir
      localStorage.clear();
      sessionStorage.clear();
      onLogout();
      navigate('/login', { replace: true });
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
        minHeight: { xs: 56, sm: 64 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: 'white', fontSize: 28 }} />
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.1rem' },
            }}
          >
            SUNAT Facturación
          </Typography>
        </Box>
      </Toolbar>
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Footer en el Drawer */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(30, 64, 175, 0.1)',
        backgroundColor: 'rgba(30, 64, 175, 0.02)',
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.7rem',
            lineHeight: 1.4,
          }}
        >
          Desarrollado por
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            textAlign: 'center',
            color: 'primary.main',
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        >
          Kreando Software SAC
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.65rem',
            mt: 0.5,
          }}
        >
          © {new Date().getFullYear()} Todos los derechos reservados
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          minHeight: { xs: 56, sm: 64 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', sm: '1.35rem' },
                  letterSpacing: '0.5px',
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                Sistema de Facturación Electrónica
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: { xs: 'none', sm: 'block' },
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
              >
                Gestión de Comprobantes SUNAT
              </Typography>
            </Box>
          </Box>
          <Button 
            color="inherit" 
            startIcon={<LogoutIcon />} 
            onClick={handleLogout}
            sx={{
              fontWeight: 600,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Salir</Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              height: '100%',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              height: '100%',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
        
        {/* Footer principal */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 3,
            mt: 'auto',
            backgroundColor: 'rgba(30, 64, 175, 0.03)',
            borderTop: '1px solid rgba(30, 64, 175, 0.1)',
          }}
        >
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            Desarrollado por{' '}
            <Box 
              component="span" 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 700,
              }}
            >
              Kreando Software SAC
            </Box>
            {' '}© {new Date().getFullYear()} - Todos los derechos reservados
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
